import { ChangeDetectionStrategy, Component, ComponentRef, Inject, OnDestroy } from '@angular/core'
import { ActivatedRoute, convertToParamMap, NavigationEnd, ParamMap, Router } from '@angular/router'
import {
  EntityHubLayout,
  MessageModel,
  MultipleSectionsViewModel,
  FilterablePartial,
  LinkModel,
  GridContainerPartial,
  VideoPlayerCard,
  ContainerPartial,
  ApiCallModel,
  AnalyticsModel,
  FilterableGridPartial,
  SEOMetadata,
  HtmlMetaElement,
  FilterOptionModel
} from '@flocasts/experience-service-types'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  pairwise,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs'
import { environment } from 'src/environments/environment'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { AdComponentInputs } from '../shared/ads/ad/ad-component.interface'
import { AdComponent } from '../shared/ads/ad/ad.component'
import {
  AD_SKIN_SIZE,
  AD_LEADERBOARD_LARGE_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_ADHESION_SIZE,
  AD_RECTANGLE_SMALL_SIZE
} from '../shared/ads/ads.utility'
import { AdTypes, AdTargeting, EqAdTargeting, AdConfig, HasInstance } from '../shared/ads/advertising-service/ad-config'
import { UpdateContentTargetingService } from '../shared/ads/advertising-service/update-content-targeting.service'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import {
  isContainerPartial,
  isEntityHubLayout,
  isFilterablePartial,
  isGridContainerPartial,
  isLinkModel,
  isMessageModel,
  isMultipleSectionsViewModel,
  isVideoPlayerCardPartial,
  notNullOrUndefined
} from '../shared/functions/type-guards'
import { AuthService } from '../singleton-services/auth/auth.service'
import { EntityHubDataService } from './entity-hub-data.service'
import {
  appendPaginatedItems,
  getId,
  getEntityHubTab,
  somethingInTheRouteChanged,
  handleBrowserBackButton,
  userIsOnScheduleTab,
  isTeamEntityType,
  userIsOnNewScheduleTab
} from './entity-hub.utility'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { SegmentEvents } from '../shared/analytics/models/segment-events.model'
import { DEFAULT_OG_IMAGE_HEIGHT, DEFAULT_OG_IMAGE_WIDTH, PageMetaService } from '../libs/page-meta/page-meta.service'
import { WINDOW } from '../app.injections'
import { PlatformService } from '../singleton-services/platform.service'
import {
  EXP_NEW_ATHLETE_PROFILES_ID,
  EXP_NEW_ATHLETE_PROFILES_VARIATION,
  EXP_NEW_COLLECTION_ENTITY_HUBS_ID,
  EXP_NEW_COLLECTION_ENTITY_HUBS_VARIATION
} from '../shared/experimentation/experiment.model'
import { SegmentTagV2, SegmentPageProperties } from '../shared/analytics/models/segment-tag.model'
import { PartialsDataService } from '../singleton-services/partials/partials-data.service'
import { FavoriteService } from '../singleton-services/user-preferences/favorite.service'
import { UpdateHeadService } from '../singleton-services/seo/update-head.service'
import { STRUCTURED_SCHEMA_ID, mapSchemaTypeFromBFF } from '../singleton-services/seo/seo.utility'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './entity-hub-smart.component.html'
})
export class EntityHubSmartComponent implements OnDestroy {
  constructor(
    private readonly ar: ActivatedRoute,
    private readonly es: ExperimentationService,
    private readonly entityHubDataService: EntityHubDataService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly updateAdTargeting: UpdateContentTargetingService,
    private readonly pageMetaService: PageMetaService,
    private readonly segmentService: SegmentService,
    private readonly ps: PlatformService,
    private readonly partialsDataService: PartialsDataService,
    private readonly favoriteService: FavoriteService,
    private readonly headService: UpdateHeadService,
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService,
    @Inject(WINDOW) private readonly window: Window
  ) {}
  private _id: number | null
  private _queryParams: ParamMap | null
  private _tab?: string | null
  private _tabs?: LinkModel[] | null
  private tabSubject$ = new BehaviorSubject<LinkModel[] | null>(null)
  /**
   * Determines if we make a partial request vs layout request.
   * If true, we make a partial request because the user is on the same entity hub/collection
   * with the same collection id and tab.
   * If false, we make an entity hub layout request to get the header because this is a new
   * navigation to this particular collection id and tab.
   * On SSR for a standings tab with facets, for example, isSameEntityHub would be false and
   * a requeset for the full entity hub layout would be made with the standings tab and the facets query param.
   * Selecting a new filter on the standings tab, isSameEntityHub would be true and
   * a request for the standings partial would be made with the new facets query param.
   */
  private _isSameEntityHub = false

  private readonly ngOnDestroy$ = new Subject<void>()

  private tabChanged$ = new Subject<LinkModel | undefined>()

  /**
   * Triggered whenever content is loading in a tab.
   */
  public readonly isLoading$ = new BehaviorSubject<boolean>(false)

  // if layout service fails, then an error will show instead of the entity hub layout
  public readonly isLayoutError$ = new BehaviorSubject<boolean>(false)

  // if partial service fails, then an error will show only in the main content
  public readonly isPartialError$ = new BehaviorSubject<boolean>(false)

  public handleTabClicked(tab: LinkModel) {
    this.tabChanged$.next(tab)
  }

  public handleToggleClicked(toggleOption: FilterOptionModel) {
    if (toggleOption.action.url) this.router.navigateByUrl(toggleOption.action.url)
  }

  private paginate$ = new Subject<LinkModel | undefined>()

  public handleNextPage(link: LinkModel | undefined) {
    this.paginate$.next(link)
  }

  private headerCta1ButtonClick$ = new Subject<LinkModel | undefined>()

  public handleHeaderCta1ButtonClick(link: LinkModel) {
    this.headerCta1ButtonClick$.next(link)
  }

  // The browser back/forward selected tab
  public selectedTab$ = new Subject<LinkModel | undefined>()

  /**
   * This stream gets the child param from the parent route, which is actually quite difficult to do in Angular.
   */
  private childParams$ = this.router.events.pipe(
    tap(e => {
      const selectedTab = handleBrowserBackButton(e, this._tabs || [])
      if (!!selectedTab) this.selectedTab$.next(selectedTab)
    }),
    filter(e => e instanceof NavigationEnd),
    startWith([]),
    switchMap(_ => this.ar.firstChild?.paramMap || of(undefined)),
    takeUntil(this.ngOnDestroy$),
    distinctUntilChanged()
  )

  public hideRightRail$ = combineLatest([this.tabSubject$, this.childParams$]).pipe(
    switchMap(([tabs, childParams]) => {
      const tab = childParams ? childParams.get('tab') ?? null : null
      if (tab) {
        return of(tab === 'events')
      }
      return of(!!this._tabs && userIsOnNewScheduleTab(this._tabs))
    })
  )

  readonly updatePageMeta = (seo: SEOMetadata) => {
    this.pageMetaService.setIsMetaClearable(true)
    this.pageMetaService.clearMeta()
    this.pageMetaService.setPageMeta({
      type: seo.ogType === 'website' ? seo.ogType : 'website',
      title: seo.title || '',
      description: seo.description || '',
      canonicalUrl: seo.canonicalUrl || '',
      siteName: null,
      twitterCard: seo.card === 'summary_large_image' ? seo.card : 'summary_large_image',
      image: {
        url: seo.imageUrl || '',
        height: Number(seo.imageHeight) || DEFAULT_OG_IMAGE_HEIGHT,
        width: Number(seo.imageWidth) || DEFAULT_OG_IMAGE_WIDTH
      }
    })
    this.pageMetaService.setIsMetaClearable(false)
  }

  readonly addSchemaMetaData = (schema: HtmlMetaElement | null) => {
    if (!schema) return
    this.headService.addElementToHead(mapSchemaTypeFromBFF(schema))
  }

  /**
   * This secondary stream gets the video player, or any other future selected item that we want to highlight at the top of the page.
   * It is a separate stream from entityHubResponse$ so it does not conflict with the auto scroll to the top of the page.
   */
  public selectedItem$: Observable<VideoPlayerCard | undefined> = this.ar.queryParamMap.pipe(
    map(q => q?.get('playing')),
    switchMap(playing => {
      return !isNaN(Number(playing)) && Number(playing) > 0
        ? this.partialsDataService
            .getVideoPlayerCardPartial(Number(playing))
            .pipe(map(res => (isVideoPlayerCardPartial(res) ? res : undefined)))
        : of(undefined)
    }),
    tap(res => {
      if (res && !!res.seo) {
        this.updatePageMeta(res.seo)
      }
    }),
    map(res => {
      if (res) {
        return res.data
      }
      return
    }),
    takeUntil(this.ngOnDestroy$)
  )

  public shouldShowNewEntityHub$ = combineLatest([
    of(isTeamEntityType(this.router.url)),
    this.es.isInVariation(
      EXP_NEW_ATHLETE_PROFILES_ID,
      EXP_NEW_ATHLETE_PROFILES_VARIATION,
      this.router.url.startsWith('/people/')
    ),
    this.es.isInVariation(
      EXP_NEW_COLLECTION_ENTITY_HUBS_ID,
      EXP_NEW_COLLECTION_ENTITY_HUBS_VARIATION,
      !!this.router.url.match(/^\/collections\/(\d+)/)
    )
  ]).pipe(
    switchMap(([isTeamEntity, isInAthletesVariation, isInCollectionsVariation]) => {
      return of(
        isTeamEntity ||
          (isInAthletesVariation && this.router.url.startsWith('/people/')) ||
          (isInCollectionsVariation && !!this.router.url.match(/^\/collections\/(\d+)/))
      )
    }),
    shareReplay(1)
  )

  /**
   * One primary reactive stream to respond to most route change events and fire appropriate API requests
   */
  private entityHubResponse$: Observable<
    | EntityHubLayout
    | FilterablePartial
    | GridContainerPartial
    | MultipleSectionsViewModel
    | ContainerPartial
    | undefined
  > = combineLatest([
    this.shouldShowNewEntityHub$,
    this.ar.queryParamMap,
    this.ar.paramMap,
    this.childParams$,
    this.tabChanged$.pipe(startWith(undefined))
  ]).pipe(
    // We must startWith because pairwise requires two values to emit, otherwise we never get data for the page.
    startWith([]),
    // We use pairwise bc the childParams$/router.event stream is chatty, causing several dupe api calls.
    pairwise(),
    // Now we can filter out dupe tab navigation events and prevent dupe api calls.
    filter(([[, oldQueryParams, oldParams, oldChildParams], [isInVariation, queryParams, params, childParams]]) => {
      this._isSameEntityHub =
        !!childParams?.get('tab') &&
        oldChildParams?.get('tab') === childParams?.get('tab') &&
        getId(params.get('collectionId') || params.get('nodeId')) ===
          getId(oldParams.get('collectionId') || oldParams.get('nodeId'))
      return (
        !!isInVariation &&
        somethingInTheRouteChanged(
          oldQueryParams || convertToParamMap({}),
          queryParams || convertToParamMap({}),
          oldChildParams || convertToParamMap({}),
          childParams || convertToParamMap({}),
          oldParams || convertToParamMap({}),
          params || convertToParamMap({})
        )
      )
    }),
    tap(_ => this.isLoading$.next(true)),
    switchMap(([[, , oldParams], [, queryParams, params, childParams, tabChanged]]) => {
      this.scrollToTop(params, oldParams)
      // Reset pagination
      this.paginate$.next(undefined)
      // Reset tab
      this.tabChanged$.next(undefined)
      // Reset Favorite button
      this.headerCta1ButtonClick$.next(undefined)
      // Fetch new data
      return this.getEntityHubExperience(params, queryParams, !!tabChanged, childParams)
    }),
    tap(_ => this.isLoading$.next(false)),
    shareReplay(1)
  )

  /**
   * If coreNodeId query param exists, perform a one-time auto-favorite of the collection on behalf of the user.
   * This code runs after a user is redirected back to the collection they were attempting to favorite
   * as an anonymous user.
   * @param apiCall The user preferences API request.
   * @param queryParams The query params of the current page context.
   * @param id The id param of the current page context.
   * @param tab The tab param of the current page context.
   * @returns An observable of the refreshed Entity Hub Layout.
   */
  private favoriteCollection(
    apiCall: ApiCallModel | null | undefined
  ): Observable<EntityHubLayout | undefined> | undefined {
    if (!!this._queryParams?.get('coreNodeId') && !!apiCall) {
      const originalQueryParams = this._queryParams?.keys.reduce((prev, curr) => {
        return { ...prev, [curr]: this._queryParams?.get(curr) }
      }, {})
      return this.entityHubDataService
        .favoriteAndRefetchEntityHubLayout(apiCall, this._id, this._tab, this._queryParams)
        .pipe(
          tap(_ => {
            // Clear the coreNodeId qp so we don't re-run this code.
            this.router.navigate([], {
              queryParams: {
                ...originalQueryParams,
                coreNodeId: null
              }
            })
          })
        )
    }
    return undefined
  }

  /**
   * Route logic for firing different api requests.
   * @param params The parent params always available: collectionId, nodeId or collectionTag.
   * @param queryParams The query params, ex: facets.
   * @param tabChanged The tab changed event, so we know to fire the partial api request.
   * @param childParams The child params, ex: the tab, like videos, events, articles, etc.
   * @paginate paginate The pagination endpoint, ex: '/api/web/entity-hub/1234/schedule/partial?limit=10&offset=20
   * @returns The api request stream.
   */
  private getEntityHubExperience(params: ParamMap, queryParams: ParamMap, tabChanged: boolean, childParams?: ParamMap) {
    this._queryParams = queryParams
    this._tab = getEntityHubTab(childParams?.get('tab'))
    this._id = getId(params.get('collectionId') || params.get('nodeId'))

    if (
      !!this._id &&
      !!this._tab &&
      (tabChanged || this._isSameEntityHub || this._queryParams?.get('viewMode') === 'minimal')
    ) {
      return this.entityHubDataService.getEntityHubPartial(this._id, this._tab, this._queryParams).pipe(
        tap(partialRes => {
          !partialRes ? this.isPartialError$.next(true) : this.isPartialError$.next(false)
        })
      )
    }
    if (!!this._id) {
      return this.entityHubDataService.getEntityHubLayoutWithCacheWrapper(this._id, this._tab, this._queryParams).pipe(
        tap(layoutRes => {
          !layoutRes ? this.isLayoutError$.next(true) : this.isLayoutError$.next(false)
        }),
        switchMap(res => {
          return this.favoriteCollection(res?.header?.cta1?.apiCall) || of(res)
        })
      )
    }

    return of(undefined)
  }

  public layout$: Observable<EntityHubLayout | undefined> = combineLatest([
    this.entityHubResponse$,
    this.headerCta1ButtonClick$.pipe(startWith(undefined))
  ]).pipe(
    switchMap(([res, cta]) => {
      if (!!cta?.apiCall) {
        this.favoriteService.setHasUpdatedFavorites(true)
        return this.entityHubDataService.favoriteAndRefetchEntityHubLayout(
          cta.apiCall,
          this._id,
          this._tab,
          this._queryParams
        )
      }

      if (res && isEntityHubLayout(res)) {
        this._tabs = res.nav.items.filter(isLinkModel)
        this.tabSubject$.next(this._tabs)
        return of(res)
      }
      return of(undefined)
    }),
    filter(notNullOrUndefined),
    shareReplay(1)
  )

  /**
   * This secondary stream's responsibility is to make paginated request and append items to the partial container.
   * If there is no paging triggered, it should just return the unmutated response.
   */
  private paginatedResponse$: Observable<
    | EntityHubLayout
    | MultipleSectionsViewModel
    | FilterablePartial
    | FilterableGridPartial
    | GridContainerPartial
    | ContainerPartial
    | undefined
  > = combineLatest([
    this.entityHubResponse$,
    this.paginate$.pipe(
      startWith(undefined),
      switchMap(link => {
        if (!!link?.url) {
          this.segmentService.trackEvent({
            name: SegmentEvents.INFINITE_SCROLL,
            properties: {
              page: link.analytics?.extraContext?.page
            }
          })
          return this.entityHubDataService.getEntityHubPaginatedPartial(link.url)
        }
        return of(undefined)
      })
    )
  ]).pipe(
    scan((acc, [entityHubResponse, curr]) => {
      return appendPaginatedItems(acc, curr, entityHubResponse)
    }, undefined)
  )

  /**
   * If nodeId or collectionId route params change,
   * we can assume we are navigating to entity hub for the first time
   * and scroll up to the top.
   * @param newParams The most recent route params.
   * @param oldParams The previous route params.
   */
  private scrollToTop = (newParams: ParamMap, oldParams: ParamMap) => {
    if (
      this.ps.isBrowser &&
      getId(newParams?.get('collectionId') || newParams?.get('nodeId')) !==
        getId(oldParams?.get('collectionId') || oldParams?.get('nodeId'))
    ) {
      this.window.scroll(0, 0)
    }
  }

  public partial$: Observable<FilterablePartial | GridContainerPartial | ContainerPartial | undefined> =
    this.paginatedResponse$.pipe(
      map(res => {
        if (isFilterablePartial(res) || isGridContainerPartial(res) || isContainerPartial(res)) {
          return res
        }
        return
      }),
      shareReplay(1)
    )

  public message$: Observable<MessageModel | undefined> = this.entityHubResponse$.pipe(
    map(res => {
      if (res && isMultipleSectionsViewModel(res)) {
        return res.sections.find(isMessageModel)
      }
      return
    })
  )

  // Ads
  private readonly loadedAds$ = new BehaviorSubject<AdComponent[]>([])
  private readonly adConfigMap: Map<AdTypes, AdComponentInputs> = new Map()
  public readonly adLoadedOnChildView = (loadedAd: HasInstance<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }
  private readonly adTargeting$: Observable<AdTargeting> = combineLatest([
    this.layout$,
    this.authService.isPremium$
  ]).pipe(
    map(([entityHubResponse, isPremium]) => {
      if (!!entityHubResponse && isEntityHubLayout(entityHubResponse)) {
        return {
          contentIds: entityHubResponse.adTargeting.aggregatedNodeIds,
          categorySlugs: entityHubResponse.adTargeting.aggregatedCategorySlugs,
          isPremium: entityHubResponse.adTargeting.isUserPremium
        }
      }

      // We must return something or the page breaks.
      // In the future we might want to add `adTargeting` to the partials (in addition to the main layout).
      return {
        isPremium
      }
    }),
    distinctUntilChanged(EqAdTargeting.equals),
    shareReplay(1)
  )

  private refreshAds$ = this.adTargeting$
    .pipe(
      withLatestFrom(this.loadedAds$),
      switchMap(args => {
        const [targeting, loadedAds] = args
        this.log.trace('Entity Collections: updating ad targeting for subsequent view')
        return this.updateAdTargeting.updateLoadedAdsTargeting(targeting, loadedAds).pipe(map(_ => args))
      }),
      switchMap(([targeting, loadedAds]) => {
        this.log.trace('Entity Collections: ad refresh triggered for subsequent view', {
          targeting
        })
        return this.updateAdTargeting.refreshLoadedAds(loadedAds)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public readonly AdComponent = import('../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly loadedAdsPush = (loadedAd: ComponentRef<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }

  public readonly adConfigs$: Observable<Map<AdTypes, AdComponentInputs>> = this.adTargeting$.pipe(
    map(targeting => {
      this.adConfigMap.set('skin', {
        config: {
          adConfig: AdConfig('collections/atf', AD_SKIN_SIZE, 'collection-skin', targeting),
          additionalOpts: {
            deferClick: environment.advertising.preventClickTimeout
          }
        }
      })

      this.adConfigMap.set('leaderboard-large', {
        config: {
          adConfig: AdConfig('collections/728x90_flex', AD_LEADERBOARD_LARGE_SIZE, 'leaderboard-large', targeting),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      this.adConfigMap.set('leaderboard-small', {
        config: {
          adConfig: AdConfig('collections/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-small', targeting),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      // Used on medium breakpoint as sticky adhesion ad
      this.adConfigMap.set('content-mobile', {
        config: {
          adConfig: AdConfig('collections/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'content-mobile', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      this.adConfigMap.set('adhesion', {
        config: {
          adConfig: AdConfig('collections/adhesion', AD_ADHESION_SIZE, 'adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      this.adConfigMap.set('rectangle-mobile', {
        config: {
          adConfig: AdConfig('collections/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'rectangle-mobile', targeting)
        }
      })

      return this.adConfigMap
    })
  )

  /**
   * This stream is responsible for updating the page head with the appropriate meta tags when the layout changes.
   */
  private readonly updatePageHeadOnLayout$ = this.layout$
    .pipe(
      tap(res => {
        if (!!res?.seo) {
          this.updatePageMeta(res.seo)
          this.addSchemaMetaData(res.seo.schema)
        }
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  /**
   * This stream is responsible for updating the page head with the appropriate meta tags when the partial changes.
   */
  private readonly updatePageHeadOnPartial$ = this.partial$
    .pipe(
      distinctUntilChanged((prev, curr) => prev?.seo?.title === curr?.seo?.title),
      tap(res => {
        if (!!res?.seo) this.updatePageMeta(res.seo)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  private readonly trackSegmentPage = combineLatest([this.layout$, this.authService.loggedIn$])
    .pipe(
      // If the user is logged in, filter out any layout emissions that do not include an isFavorite property.
      // This property should always be getting manually set in the BFF and returned in the layout response when
      // the user is logged in. If the user is logged out, proceed as normal.
      filter(([layout, loggedIn]) => {
        return loggedIn ? !!layout?.analytics?.extraContext && 'isFavorite' in layout?.analytics?.extraContext : true
      }),
      map(([layout, _]) => layout),
      filter((layout: EntityHubLayout) => !!layout?.analytics),
      // Distinct is necessary so segment page calls do not fire multiple times.
      distinctUntilChanged((prev, curr) => prev.analytics.entityId === curr.analytics.entityId),
      map(layout => layout.analytics),
      tap((analytics: AnalyticsModel) => {
        const segmentTag: SegmentTagV2 = {
          page_category: analytics.extraContext?.pageCategory || 'Collection',
          subpage_category: analytics.extraContext?.subpageCategory,
          // @ts-ignore category is required on web, but nullable in the BFF
          category: analytics.entityType || null,
          node_type: analytics.entityType || undefined,
          autoPageCall: false
        }

        const segmentPageProperties: SegmentPageProperties = {
          _tag: 'collection',
          node_id: analytics.entityId || undefined,
          content_tags: analytics.extraContext?.contentTags || undefined,
          is_favorite: analytics.extraContext?.isFavorite
        }

        this.segmentService.pageV2(segmentTag, segmentPageProperties)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.loadedAds$.complete()
    this.tabChanged$.complete()
    this.paginate$.complete()
    this.isLoading$.complete()
    this.isLayoutError$.complete()
    this.isPartialError$.complete()
    this.headerCta1ButtonClick$.complete()
    this.pageMetaService.clearMeta()
    this.headService.removeElement(STRUCTURED_SCHEMA_ID)
  }
}
